import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiActivity,
  FiCalendar,
  FiPieChart,
  FiTrendingUp,
  FiMessageSquare,
  FiUsers,
  FiTarget,
  FiZap,
  FiCheckSquare,
  FiBarChart2,
  FiClock,
  FiAward
} from 'react-icons/fi';
import './CoachifyPages.css';
import CoachifyLogo from '../../assets/logos/CoachifyHoriLogo.png';

const Features = () => {
  const features = [
    {
      category: 'Nutrition Management',
      items: [
        {
          icon: <FiPieChart />,
          title: 'Advanced Macro Calculator',
          description: 'Science-based calculations using Mifflin-St Jeor equation with customizable goals for cutting, bulking, or maintenance.'
        },
        {
          icon: <FiCalendar />,
          title: 'Meal Planning System',
          description: 'Drag-and-drop meal builder with templates, favorite foods, and automatic macro calculations for weekly planning.'
        },
        {
          icon: <FiCheckSquare />,
          title: 'Food Logging & Tracking',
          description: 'Comprehensive daily meal tracking with real-time macro calculations, water intake monitoring, and barcode scanning.'
        }
      ]
    },
    {
      category: 'Training Programs',
      items: [
        {
          icon: <FiActivity />,
          title: 'Program Wizard',
          description: 'Create sophisticated workout programs with exercise progression tracking, set/rep/weight configuration, and difficulty levels.'
        },
        {
          icon: <FiZap />,
          title: 'Exercise Library',
          description: 'Extensive exercise database with detailed information, categorization, and quick selection for program building.'
        },
        {
          icon: <FiUsers />,
          title: 'Multi-Client Assignment',
          description: 'Assign single programs to multiple clients simultaneously. Save time with bulk operations and program templates.'
        }
      ]
    },
    {
      category: 'Client Management',
      items: [
        {
          icon: <FiUsers />,
          title: 'Client Invitation System',
          description: 'Email-based invitations with unique codes, automatic trainer-client linking, and zero-friction onboarding.'
        },
        {
          icon: <FiMessageSquare />,
          title: 'Integrated Messaging',
          description: 'Real-time chat between trainers and clients with message history, unread tracking, and conversation management.'
        },
        {
          icon: <FiTrendingUp />,
          title: 'Progress Analytics',
          description: 'Track weight, body fat, measurements, compliance rates, and adherence with visual charts and trend analysis.'
        }
      ]
    },
    {
      category: 'Business Intelligence',
      items: [
        {
          icon: <FiBarChart2 />,
          title: 'Analytics Dashboard',
          description: 'Client growth trends, program performance metrics, session completion rates, and retention analytics.'
        },
        {
          icon: <FiAward />,
          title: 'Client Satisfaction Tracking',
          description: 'Collect and analyze client feedback with star ratings, review management, and satisfaction metrics.'
        },
        {
          icon: <FiClock />,
          title: 'Calendar & Scheduling',
          description: 'Advanced calendar with session management, client assignments, event tracking, and automated reminders.'
        }
      ]
    },
    {
      category: 'Efficiency Tools',
      items: [
        {
          icon: <FiTarget />,
          title: 'Template System',
          description: 'Reusable workout and nutrition plan templates. Clone, duplicate, and customize to save hours of planning time.'
        },
        {
          icon: <FiCheckSquare />,
          title: 'Quick Actions',
          description: 'Streamlined workflows for common tasks: log workouts, view meal plans, message clients, update progress.'
        },
        {
          icon: <FiActivity />,
          title: 'Real-Time Updates',
          description: 'Firebase-powered real-time data synchronization ensures trainers and clients always see the latest information.'
        }
      ]
    }
  ];

  return (
    <div className="coachify-page">
      {/* Header Navigation */}
      <header className="coachify-header">
        <div className="header-logo">
          <Link to="/" className="logo-link">
            <img src={CoachifyLogo} alt="Coachify" className="header-logo-img" />
          </Link>
        </div>
        <nav className="coachify-nav">
          <Link to="/about" className="nav-link">About</Link>
          <Link to="/features" className="nav-link active">Features</Link>
          <Link to="/pricing" className="nav-link">Pricing</Link>
          <Link to="/contact" className="nav-link">Contact</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-container">
          <h1 className="page-title">Everything Your Gym Needs to Succeed</h1>
          <p className="page-subtitle">
            A comprehensive platform that combines nutrition science, fitness programming,
            and business intelligence in one powerful solution.
          </p>
        </div>
      </section>

      {/* Features Content */}
      <section className="page-content">
        <div className="page-container">
          {features.map((category, categoryIndex) => (
            <div key={categoryIndex} className="content-section">
              <h2 className="category-title">{category.category}</h2>
              <div className="features-grid">
                {category.items.map((feature, featureIndex) => (
                  <div key={featureIndex} className="feature-box">
                    <div className="feature-icon">
                      {feature.icon}
                    </div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-text">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Key Differentiators */}
          <div className="content-section highlight-section">
            <h2 className="section-title">What Sets Coachify Apart</h2>
            <div className="highlight-grid">
              <div className="highlight-item">
                <h3 className="highlight-title">White-Label Solution</h3>
                <p className="highlight-text">
                  Your brand, your platform. Coachify becomes your gym's proprietary system,
                  strengthening member loyalty and market differentiation.
                </p>
              </div>
              <div className="highlight-item">
                <h3 className="highlight-title">Complete Integration</h3>
                <p className="highlight-text">
                  Unlike fragmented solutions, Coachify unifies nutrition tracking, workout
                  programming, client communication, and business analytics in one platform.
                </p>
              </div>
              <div className="highlight-item">
                <h3 className="highlight-title">Scalable by Design</h3>
                <p className="highlight-text">
                  Template systems, bulk operations, and automation mean your coaches can
                  manage more clients without sacrificing personalization or quality.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="content-section cta-section">
            <h2 className="section-title">See Coachify in Action</h2>
            <p className="section-text">
              Schedule a personalized demo to see how Coachify can transform your gym's coaching delivery.
            </p>
            <Link to="/contact" className="cta-button">Request a Demo</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="coachify-footer">
        <p>&copy; 2025 Coachify. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Features;
